/**
 * The chat transport: one turn, streamed from `POST /api/chat`.
 *
 * It carries no credential of its own. The session cookie is HttpOnly and the
 * browser attaches it, which is why `credentials: 'include'` is the only auth
 * here — the bundle has held no secret since C017, and the OpenClaw gateway
 * token this replaced was one the app shipped to every browser that loaded it.
 */

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export type ChatStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'done'; costUsd: number }
  | { type: 'error'; code: string; message: string };

export interface ChatRequest {
  message: string;
  history: { role: 'user' | 'assistant'; content: string }[];
}

export interface StreamChatOptions {
  signal?: AbortSignal;
  /** Injected in tests; the global otherwise. */
  fetch?: typeof fetch;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string };
}

const GENERIC_FAILURE = 'Ugin could not answer that just now.';

/**
 * One SSE frame into an event. Unknown frame names are dropped rather than
 * guessed at: a new server frame should be inert in an old tab, not fatal.
 */
function toEvent(name: string, data: string): ChatStreamEvent | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  if (name === 'delta') {
    const { text } = parsed as { text?: unknown };
    return typeof text === 'string' ? { type: 'delta', text } : null;
  }
  if (name === 'done') {
    const { costUsd } = parsed as { costUsd?: unknown };
    return { type: 'done', costUsd: typeof costUsd === 'number' ? costUsd : 0 };
  }
  if (name === 'error') {
    const { code, message } = parsed as { code?: unknown; message?: unknown };
    return {
      type: 'error',
      code: typeof code === 'string' ? code : 'chat_failed',
      message: typeof message === 'string' ? message : GENERIC_FAILURE,
    };
  }
  return null;
}

/** Whole frames out of a buffer, leaving any partial tail behind. */
function takeFrames(buffer: string): { frames: string[]; rest: string } {
  const parts = buffer.split('\n\n');
  return { frames: parts.slice(0, -1), rest: parts[parts.length - 1] ?? '' };
}

function parseFrame(frame: string): ChatStreamEvent | null {
  let name = 'message';
  const data: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) name = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trim());
  }
  return data.length === 0 ? null : toEvent(name, data.join('\n'));
}

const ABORTED = Symbol('aborted');

/** Resolves when the signal fires, and never otherwise. */
function aborted(signal: AbortSignal | undefined): Promise<typeof ABORTED> {
  if (signal === undefined) return new Promise<typeof ABORTED>(() => undefined);
  if (signal.aborted) return Promise.resolve(ABORTED);
  return new Promise<typeof ABORTED>((resolve) => {
    signal.addEventListener('abort', () => { resolve(ABORTED); }, { once: true });
  });
}

async function errorEvent(response: Response): Promise<ChatStreamEvent> {
  let body: ErrorEnvelope | null = null;
  try {
    body = (await response.json()) as ErrorEnvelope;
  } catch {
    body = null;
  }
  return {
    type: 'error',
    code: body?.error?.code ?? `http_${String(response.status)}`,
    message: body?.error?.message ?? GENERIC_FAILURE,
  };
}

export async function* streamChat(
  request: ChatRequest,
  options: StreamChatOptions = {},
): AsyncGenerator<ChatStreamEvent> {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  let response: Response;
  try {
    response = await doFetch('/api/chat', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
      body: JSON.stringify(request),
      credentials: 'include',
      signal: options.signal ?? null,
    });
  } catch (err) {
    if (options.signal?.aborted === true) return;
    yield {
      type: 'error',
      code: 'network_error',
      message: err instanceof Error ? err.message : GENERIC_FAILURE,
    };
    return;
  }

  if (!response.ok) {
    yield await errorEvent(response);
    return;
  }
  if (response.body === null) {
    yield { type: 'error', code: 'no_body', message: GENERIC_FAILURE };
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      // Two checks, because they answer different questions. This one settles
      // the case where the abort landed while the caller was handling the
      // last event: with both promises already resolved, a race is a race.
      if (options.signal?.aborted === true) return;
      // Raced against the signal rather than merely handed to fetch. Stop has
      // to stop: how quickly a transport notices an abort is its business,
      // and a pending read that never settles would leave the caller saying
      // "Thinking…" over a reply that is never coming.
      const next = await Promise.race([reader.read(), aborted(options.signal)]);
      if (next === ABORTED) return;
      const { done, value } = next;
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // A network splits wherever it likes, so frames are taken out of a
      // buffer rather than assumed to arrive one per chunk.
      const { frames, rest } = takeFrames(buffer);
      buffer = rest;
      for (const frame of frames) {
        const event = parseFrame(frame);
        if (event !== null) yield event;
      }
    }
  } catch (err) {
    // Stopping was the reader's own doing; reporting it back as a failure is
    // noise on top of an action they took deliberately.
    if (options.signal?.aborted === true) return;
    yield {
      type: 'error',
      code: 'stream_failed',
      message: err instanceof Error ? err.message : GENERIC_FAILURE,
    };
  } finally {
    await reader.cancel().catch(() => undefined);
  }
}
